import { Link, Navigate, useParams } from 'react-router-dom';
import Button from '../../components/base/Button';
import Seo from '../../components/feature/Seo';
import { getServicePage, PHONE_HREF, servicePages, WHATSAPP_HREF } from '../../lib/schiek-content';

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const service = getServicePage(slug);

  if (!service) {
    return <Navigate to="/leistungen" replace />;
  }

  return (
    <main className="bg-white pt-28 pb-20">
      <Seo title={service.metaTitle} description={service.metaDescription} path={`/leistungen/${service.slug}`} />

      <section className="px-5" aria-labelledby="service-detail-heading">
        <div className="mx-auto max-w-6xl">
          <div className="grid gap-8 rounded-[2rem] border border-slate-200 bg-slate-50 p-6 shadow-[0_22px_70px_rgba(15,23,42,0.08)] md:grid-cols-[1.1fr_0.9fr] md:p-9">
            <div>
              <p className="mb-4 text-xs font-semibold uppercase tracking-[0.24em] text-[#0F3D8C]">
                {service.eyebrow}
              </p>
              <h1 id="service-detail-heading" className="text-3xl font-semibold leading-tight text-slate-950 sm:text-5xl">
                {service.title}
              </h1>
              <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-700 sm:text-lg">
                {service.summary}
              </p>
              <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                <Button href={PHONE_HREF} size="lg" className="whitespace-normal px-6 text-sm">
                  <i className="ri-phone-line mr-2 text-lg" aria-hidden="true" />
                  Jetzt anrufen
                </Button>
                <Button href={WHATSAPP_HREF} variant="secondary" size="lg" className="whitespace-normal px-6 text-sm">
                  <i className="ri-whatsapp-line mr-2 text-lg" aria-hidden="true" />
                  WhatsApp senden
                </Button>
              </div>
            </div>

            <aside className="rounded-[1.5rem] border border-[#0F3D8C]/15 bg-white p-5">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#0F3D8C]">Bereithalten</p>
              <ul className="mt-4 space-y-3 text-sm text-slate-700">
                {service.needed.map((item) => (
                  <li key={item} className="flex gap-3">
                    <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-[#0F3D8C]" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </aside>
          </div>
        </div>
      </section>

      <section className="px-5 py-16" aria-labelledby="service-help-heading">
        <div className="mx-auto grid max-w-6xl gap-8 md:grid-cols-[0.95fr_1.05fr]">
          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.22em] text-[#0F3D8C]">Wann wir helfen</p>
            <h2 id="service-help-heading" className="text-2xl font-semibold text-slate-950 sm:text-3xl">
              Klare Hilfe ohne Umweg
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-700">{service.intro}</p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {service.bullets.map((item) => (
              <div key={item} className="rounded-3xl border border-slate-200 bg-white p-5 shadow-[0_14px_35px_rgba(15,23,42,0.05)]">
                <span className="mb-4 flex h-9 w-9 items-center justify-center rounded-full bg-[#0F3D8C]/10 text-[#0F3D8C]">
                  <i className="ri-check-line" aria-hidden="true" />
                </span>
                <p className="text-sm leading-relaxed text-slate-700">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-950 px-5 py-16 text-white" aria-labelledby="service-process-heading">
        <div className="mx-auto max-w-6xl">
          <div className="max-w-3xl">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.22em] text-sky-300">Ablauf</p>
            <h2 id="service-process-heading" className="text-2xl font-semibold sm:text-3xl">
              So läuft der Einsatz ab
            </h2>
          </div>
          <div className="mt-8 grid gap-4 md:grid-cols-4">
            {service.steps.map((step, index) => (
              <article key={step} className="rounded-3xl border border-white/10 bg-white/5 p-5">
                <span className="text-xs font-semibold uppercase tracking-[0.22em] text-sky-300">
                  Schritt {index + 1}
                </span>
                <p className="mt-3 text-sm leading-relaxed text-slate-200">{step}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="px-5 py-16" aria-labelledby="service-faq-heading">
        <div className="mx-auto max-w-5xl">
          <h2 id="service-faq-heading" className="text-2xl font-semibold text-slate-950 sm:text-3xl">
            Häufige Fragen
          </h2>
          <div className="mt-7 grid gap-4">
            {service.faq.map((item) => (
              <article key={item.question} className="rounded-3xl border border-slate-200 bg-white p-5">
                <h3 className="font-semibold text-slate-950">{item.question}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-700">{item.answer}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="px-5 pb-4">
        <div className="mx-auto max-w-6xl rounded-[2rem] bg-[#0F3D8C] p-6 text-white md:p-8">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-sky-200">Alle Leistungen</p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {servicePages.map((item) => (
              <Link
                key={item.slug}
                to={`/leistungen/${item.slug}`}
                className="rounded-2xl border border-white/15 bg-white/10 p-4 text-sm font-semibold transition-colors hover:bg-white/15"
              >
                {item.shortTitle}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
